import React, { useState, useEffect, useCallback } from 'react';
import { Star, PenLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReviewFormModal from './ReviewFormModal';

const Stars = ({ n, className = 'mb-4' }) => (
  <div className={`flex gap-1 ${className}`}>
    {[...Array(5)].map((_, s) => (
      <Star key={s} size={20} className={s < n ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
    ))}
  </div>
);

const ReviewCard = ({ review, testid }) => (
  <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm flex flex-col" data-testid={testid}>
    <Stars n={review.rating} />
    <p className="text-gray-700 leading-relaxed flex-1">"{review.message}"</p>
    <p className="text-sm font-semibold text-[#0B3F84] mt-5">
      — {review.name}{review.company ? `, ${review.company}` : ''}
    </p>
  </div>
);

const Testimonials = () => {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);

  const fetchReviews = useCallback(async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/reviews`);
      const data = await res.json();
      if (data.success) {
        setReviews(data.reviews || []);
        setAvg(data.average_rating || 0);
      }
    } catch (e) {
      // silent - fall back to placeholder testimonials
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Placeholder testimonials (used only to fill the top grid until enough real reviews exist)
  const placeholders = [
    { rating: 5, message: t('trust.review1'), name: t('trust.author1'), company: null, _fake: true },
    { rating: 5, message: t('trust.review2'), name: t('trust.author2'), company: null, _fake: true },
    { rating: 5, message: t('trust.review3'), name: t('trust.author3'), company: null, _fake: true }
  ];

  const real = reviews;
  const topGrid = [...real.slice(0, 3)];
  while (topGrid.length < 3) topGrid.push(placeholders[topGrid.length]);
  const overflow = real.slice(3);
  const hasReal = real.length > 0;

  const reviewSchema = hasReal ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Digital Corp France",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": String(avg),
      "reviewCount": String(real.length),
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": real.slice(0, 10).map((r) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "reviewRating": { "@type": "Rating", "ratingValue": String(r.rating), "bestRating": "5" },
      "reviewBody": r.message
    }))
  } : null;

  return (
    <section id="avis" className="section bg-gradient-to-br from-gray-50 to-white" data-testid="trust-section">
      {reviewSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewSchema).replace(/</g, '\\u003c') }}
        />
      )}
      <div className="container">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-4">
            {t('trust.title', 'Why Companies Choose Digital Corp')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('trust.subtitle', 'A trusted technology partner for AI, SaaS and custom software projects worldwide.')}
          </p>
          {hasReal && (
            <div className="flex items-center justify-center gap-2 mt-4" data-testid="trust-aggregate">
              <Stars n={Math.round(avg)} className="mb-0" />
              <span className="text-sm text-gray-600">{avg}/5 · {real.length} {t('reviews.reviewsCount', 'reviews')}</span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {topGrid.map((r, i) => (
            <ReviewCard key={i} review={r} testid={`testimonial-${i}`} />
          ))}
        </div>

        {overflow.length > 0 && (
          <div className="mt-8 max-w-6xl mx-auto grid md:grid-cols-3 gap-8" data-testid="reviews-overflow">
            {overflow.map((r, i) => (
              <ReviewCard key={r.id || i} review={r} testid={`review-overflow-${i}`} />
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <button
            onClick={() => setIsFormOpen(true)}
            className="px-6 py-3 bg-[#0B3F84] text-white font-semibold rounded-lg hover:bg-[#0a3570] inline-flex items-center gap-2"
            data-testid="leave-review-btn"
          >
            <PenLine size={18} />
            {t('reviews.leaveReview', 'Leave a Review')}
          </button>
        </div>

        {!hasReal && (
          <p className="text-center text-xs text-gray-400 italic mt-8 max-w-2xl mx-auto">
            {t('trust.disclaimer', 'Testimonials shown for presentation purposes and may be replaced by verified client reviews.')}
          </p>
        )}
      </div>

      <ReviewFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} onSubmitted={fetchReviews} />
    </section>
  );
};

export default Testimonials;
